import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { LocalAudioTrack, LocalTrack, Track } from 'livekit-client';
import { SwitchControl } from '../../components/SwitchControl';
import { useRoom } from '../room/RoomProvider';
import { HOST_BROWSER_AUDIO_TRACK_NAME } from './browserAudio.constants';

const HOST_BROWSER_AUDIO_PRESET = {
  maxBitrate: 128_000,
  priority: 'high' as RTCPriorityType,
};
const MUSIC_SHARING_SHARE_IMAGE_SRC = '/images/audio/music-sharing-share-tab.png';
const MUSIC_SHARING_CHROME_MUTE_IMAGE_SRC = '/images/audio/music-sharing-mute-tab.jpg';
const MUSIC_SHARING_SAFARI_MUTE_IMAGE_SRC = '/images/audio/music-sharing-mute-tab-safari.jpg';
const MUSIC_SHARING_VOLUME_CONTROL_IMAGE_SRC = '/images/audio/music-sharing-volume-control.jpg';
const MUSIC_SHARING_MY_VOLUME_IMAGE_SRC = '/images/audio/music-sharing-my-volume.jpg';

type BrowserAudioStatus = 'idle' | 'starting' | 'sharing' | 'error';

interface BrowserBrand {
  brand: string;
  version: string;
}

interface UserAgentDataWithBrowserHints {
  brands?: BrowserBrand[];
  mobile?: boolean;
}

interface MusicSharingStep {
  number: string;
  text: ReactNode;
  imageSrc: string;
  imageAlt: string;
  tall?: boolean;
}

export function HostBrowserAudioControl() {
  const { localParticipant } = useLocalParticipant();
  const { socket, browserAudioSettings, updateBrowserAudioSettings } = useRoom();
  const canShareBrowserAudio = useCanShareBrowserAudio();
  const [status, setStatus] = useState<BrowserAudioStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  const capturedTracksRef = useRef<LocalTrack[]>([]);
  const publishedAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const endedListenersRef = useRef<Array<[MediaStreamTrack, () => void]>>([]);
  const socketRef = useRef(socket);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  const removeEndedListeners = useCallback(() => {
    endedListenersRef.current.forEach(([track, listener]) => {
      track.removeEventListener('ended', listener);
    });
    endedListenersRef.current = [];
  }, []);

  const cleanupCapture = useCallback(async () => {
    removeEndedListeners();

    const publishedAudioTrack = publishedAudioTrackRef.current;
    const capturedTracks = capturedTracksRef.current;

    publishedAudioTrackRef.current = null;
    capturedTracksRef.current = [];

    if (publishedAudioTrack && localParticipant) {
      try {
        const publication = await localParticipant.unpublishTrack(publishedAudioTrack, true);
        if (!publication) {
          publishedAudioTrack.stop();
        }
      } catch (error) {
        console.error('[HostBrowserAudioControl] Failed to unpublish browser audio:', error);
        publishedAudioTrack.stop();
      }
    } else {
      publishedAudioTrack?.stop();
    }

    capturedTracks.forEach((track) => {
      if (track !== publishedAudioTrack) {
        track.stop();
      }
    });

    socketRef.current?.emit('browser-audio:update', null);
  }, [localParticipant, removeEndedListeners]);

  useEffect(() => {
    if (canShareBrowserAudio) return;

    void cleanupCapture();
    setError(null);
    setStatus('idle');
  }, [canShareBrowserAudio, cleanupCapture]);

  const stopSharing = useCallback(async () => {
    await cleanupCapture();
    setError(null);
    setStatus('idle');
  }, [cleanupCapture]);

  const registerEndedListeners = useCallback((tracks: LocalTrack[]) => {
    const handleTrackEnded = () => {
      void stopSharing();
    };

    endedListenersRef.current = tracks.map((track) => {
      const mediaStreamTrack = track.mediaStreamTrack;
      mediaStreamTrack.addEventListener('ended', handleTrackEnded);
      return [mediaStreamTrack, handleTrackEnded];
    });
  }, [stopSharing]);

  const startSharing = async () => {
    if (!localParticipant || status === 'starting' || status === 'sharing') return;

    setStatus('starting');
    setError(null);

    try {
      const microphonePublicationBeforeShare = localParticipant.getTrackPublication(Track.Source.Microphone);
      const shouldKeepMicrophoneEnabled = Boolean(
        microphonePublicationBeforeShare?.track && !microphonePublicationBeforeShare.isMuted
      );
      const tracks = await localParticipant.createScreenTracks({
        audio: {
          autoGainControl: false,
          channelCount: 2,
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 48_000,
          voiceIsolation: false,
        },
        video: { displaySurface: 'browser' },
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
        suppressLocalAudioPlayback: true,
        systemAudio: 'exclude',
      });

      const audioTrack = tracks.find(
        (track): track is LocalAudioTrack => track.kind === Track.Kind.Audio
      );

      if (!audioTrack) {
        tracks.forEach((track) => track.stop());
        setError('Choose a browser tab and turn on tab audio in the sharing dialog.');
        setStatus('error');
        return;
      }

      audioTrack.mediaStreamTrack.contentHint = 'music';
      capturedTracksRef.current = tracks;
      registerEndedListeners(tracks);
      publishedAudioTrackRef.current = audioTrack;

      await localParticipant.publishTrack(audioTrack, {
        name: HOST_BROWSER_AUDIO_TRACK_NAME,
        source: Track.Source.ScreenShareAudio,
        audioPreset: HOST_BROWSER_AUDIO_PRESET,
        forceStereo: true,
        dtx: false,
        red: false,
      });

      if (shouldKeepMicrophoneEnabled) {
        const microphonePublication = localParticipant.getTrackPublication(Track.Source.Microphone);
        const microphoneTrack = microphonePublication?.track;
        const mediaStreamTrack = microphoneTrack?.mediaStreamTrack ?? null;

        if (!microphoneTrack || microphonePublication?.isMuted || !localParticipant.isMicrophoneEnabled) {
          await localParticipant.setMicrophoneEnabled(true);
        } else if (mediaStreamTrack && (mediaStreamTrack.readyState !== 'live' || mediaStreamTrack.muted)) {
          await microphoneTrack.restartTrack();
        }
      }

      setStatus('sharing');
      setShowTipsModal(true);
    } catch (error) {
      await cleanupCapture();
      console.error('[HostBrowserAudioControl] Failed to share browser audio:', error);
      setError(getShareAudioErrorMessage(error));
      setStatus('error');
    }
  };

  useEffect(() => {
    return () => {
      void cleanupCapture();
    };
  }, [cleanupCapture]);

  const isStarting = status === 'starting';
  const isSharing = status === 'sharing';

  if (!canShareBrowserAudio) {
    return null;
  }

  return (
    <>
      <div className="rounded-lg border border-line bg-panel2 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${isSharing ? 'bg-ok' : 'bg-line'}`} />
              <p className="text-[12px] font-medium text-text">Share browser audio</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSettingsPanel((isOpen) => !isOpen)}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-line bg-panel text-text-dim transition-colors hover:bg-bg-secondary hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              aria-label="Music sharing settings"
              aria-expanded={showSettingsPanel}
            >
              <SettingsIcon className="h-4 w-4" />
            </button>
            {isSharing ? (
              <button
                onClick={stopSharing}
                className="btn btn-sm bg-warn/10 text-warn hover:bg-warn/15 border-warn/30 flex-shrink-0"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={startSharing}
                disabled={isStarting || !localParticipant}
                className="btn btn-sm btn-accent flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isStarting ? 'Opening...' : 'Share'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <p className="mt-2 text-[11px] leading-snug text-warn">
            {error}
          </p>
        )}

        {showSettingsPanel && (
          <div className="mt-3 space-y-3 rounded-lg border border-line bg-panel p-3">
            <BrowserAudioSettingRow
              label="Mute artists during poses"
              checked={browserAudioSettings.autoMuteArtistsDuringPoses}
              onChange={(autoMuteArtistsDuringPoses) => updateBrowserAudioSettings({ autoMuteArtistsDuringPoses })}
            />
            <BrowserAudioSettingRow
              label="Lower volume during breaks"
              checked={browserAudioSettings.lowerMusicDuringBreaks}
              onChange={(lowerMusicDuringBreaks) => updateBrowserAudioSettings({ lowerMusicDuringBreaks })}
            />
            <a
              href="#music-sharing-tips"
              onClick={(event) => {
                event.preventDefault();
                setShowTipsModal(true);
              }}
              className="text-link text-[12px]"
            >
              How to make the music sound great
            </a>
          </div>
        )}
      </div>

      {showTipsModal && (
        <BrowserAudioTipsModal onClose={() => setShowTipsModal(false)} />
      )}
    </>
  );
}

function BrowserAudioSettingRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="min-w-0 text-[12px] leading-snug text-text">{label}</span>
      <SwitchControl checked={checked} onChange={onChange} ariaLabel={label} />
    </div>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.757.426 1.757 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.757-2.924 1.757-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.757-.426-1.757-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function getMusicSharingMuteStep(): MusicSharingStep {
  if (isSafariBrowser()) {
    return {
      number: '2',
      text: <>Open Safari&apos;s <b className="font-semibold">Window</b> menu, and select &quot;Mute This Tab&quot;.</>,
      imageSrc: MUSIC_SHARING_SAFARI_MUTE_IMAGE_SRC,
      imageAlt: 'Safari Window menu with Mute This Tab highlighted.',
      tall: true,
    };
  }

  return {
    number: '2',
    text: <>Right-click the music tab, and select &quot;Mute site&quot;.</>,
    imageSrc: MUSIC_SHARING_CHROME_MUTE_IMAGE_SRC,
    imageAlt: 'Browser tab context menu with Mute Site highlighted.',
  };
}

function BrowserAudioTipsModal({ onClose }: { onClose: () => void }) {
  const muteStep = getMusicSharingMuteStep();
  const steps: MusicSharingStep[] = [
    {
      number: '1',
      text: <>Share the tab playing the music.</>,
      imageSrc: MUSIC_SHARING_SHARE_IMAGE_SRC,
      imageAlt: 'Browser sharing dialog with Share browser audio enabled.',
    },
    muteStep,
    {
      number: '3',
      text: <>Set the music player&apos;s volume to <b className="font-semibold">100%</b>.</>,
      imageSrc: MUSIC_SHARING_VOLUME_CONTROL_IMAGE_SRC,
      imageAlt: 'Music player volume control set to full volume.',
    },
    {
      number: '4',
      text: <>Control your personal volume using the <b className="font-semibold">music slider</b>.</>,
      imageSrc: MUSIC_SHARING_MY_VOLUME_IMAGE_SRC,
      imageAlt: 'DrawClub personal music volume control.',
      tall: true,
    },
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-5 py-8 backdrop-blur-[3px]"
      onMouseDown={onClose}
    >
      <div
        className="max-h-[calc(100svh-64px)] w-full max-w-[760px] overflow-y-auto rounded-2xl border border-line bg-panel shadow-[0_24px_60px_-20px_rgba(20,20,20,0.32),0_4px_12px_rgba(20,20,20,0.08)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="browser-audio-tips-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="relative border-b border-line-soft px-6 py-6 pr-16 sm:px-10 sm:pb-6 sm:pt-8 sm:pr-20">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-faint">
            Audio setup
          </p>
          <h2 id="browser-audio-tips-title" className="type-title-modal max-w-[28ch] text-text">
            How to make music sound great
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-panel text-text-dim transition-colors hover:bg-bg-secondary hover:text-text sm:right-7 sm:top-6"
            aria-label="Close music sharing tips"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </div>

        <div className="grid bg-line-soft sm:grid-cols-2">
          {steps.map((step, index) => (
            <section
              key={step.number}
              className={`border-b border-line-soft bg-panel px-6 py-6 sm:px-8 sm:py-7 ${index % 2 === 0 ? 'sm:border-r' : ''}`}
            >
              <div className="mb-4 flex items-baseline gap-3">
                <span className="min-w-[22px] flex-shrink-0 font-display text-[18px] leading-none text-text-faint tabular-nums">
                  {step.number}
                </span>
                <p className="m-0 text-[16px] leading-6 text-text">
                  {step.text}
                </p>
              </div>
              <div className="flex min-h-[168px] items-center justify-center overflow-hidden rounded-[10px] border border-line bg-bg-secondary p-4">
                <img
                  src={step.imageSrc}
                  alt={step.imageAlt}
                  className={`h-auto w-auto max-w-full rounded ${step.tall ? 'max-h-[240px]' : 'max-h-[220px]'}`}
                />
              </div>
            </section>
          ))}
        </div>

        <div className="flex flex-col gap-4 border-t border-line-soft bg-panel px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-10 sm:py-7">
          <p className="max-w-[48ch] text-[13.5px] leading-relaxed text-text-dim">
            Everyone gets their own music volume. Volume lowers when people speak and during breaks.
          </p>
          <button type="button" onClick={onClose} className="btn btn-accent">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function useCanShareBrowserAudio() {
  const [canShareBrowserAudio, setCanShareBrowserAudio] = useState(canShareBrowserAudioFromThisDevice);

  useEffect(() => {
    setCanShareBrowserAudio(canShareBrowserAudioFromThisDevice());
  }, []);

  return canShareBrowserAudio;
}

function canShareBrowserAudioFromThisDevice() {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.mediaDevices?.getDisplayMedia) return false;

  return !isLikelyMobilePlatform() && isChromiumLikeBrowser();
}

function isLikelyMobilePlatform() {
  if (typeof navigator === 'undefined') return false;

  const userAgentData = (navigator as Navigator & { userAgentData?: UserAgentDataWithBrowserHints }).userAgentData;
  if (userAgentData?.mobile === true) return true;

  const userAgent = navigator.userAgent;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return true;
  }

  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function isSafariBrowser() {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent;
  return /Safari/i.test(userAgent) && !/\b(Chrome|Chromium|CriOS|FxiOS|Edg|OPR)\//i.test(userAgent);
}

function isChromiumLikeBrowser() {
  if (typeof navigator === 'undefined') return false;

  const userAgentData = (navigator as Navigator & { userAgentData?: UserAgentDataWithBrowserHints }).userAgentData;
  const brandNames = userAgentData?.brands?.map((brand) => brand.brand).join(' ') ?? '';
  if (/Chromium|Google Chrome|Microsoft Edge/i.test(brandNames)) return true;

  const userAgent = navigator.userAgent;
  return /\b(Chrome|Chromium|Edg|OPR)\//i.test(userAgent) && !/\b(Firefox|FxiOS)\//i.test(userAgent);
}

function getShareAudioErrorMessage(error: unknown) {
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return 'Sharing was cancelled or blocked by the browser.';
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Could not start browser tab audio.';
}
